import type { BreadcrumbList, CollectionPage, Graph, Organization, Person, Thing, WebSite } from 'schema-dts';

import config from 'js/app/config';
import Imgix from 'js/lib/imgix';

import { filterUndefined } from 'bundles/common/utils/commonUtils';
import { clearEmptyFields } from 'bundles/seo/utils/utils';
import type { AuthorProfile } from 'bundles/shared-articles/components/ArticleAuthorProfile';
import { DEFAULT_ARTICLE_AUTHOR } from 'bundles/shared-articles/constants';

import _t from 'i18n!nls/shared-articles';

export type Breadcrumb = { name: string; absoluteUrl: string };

const createGraph = (things: Array<Thing>): Graph => ({
  '@context': 'https://schema.org',
  '@graph': things.map(clearEmptyFields),
});

const createBreadcrumbsListSchema = (breadcrumbs: Array<Breadcrumb>): BreadcrumbList => {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map(({ name, absoluteUrl }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        name,
        '@id': absoluteUrl,
      },
    })),
  };
};

const createOrganizationSchema = (): Organization => ({
  '@type': 'Organization',
  name: 'Coursera',
  url: `${config.url.base}`,
  logo: Imgix.processImage('https://coursera.s3.amazonaws.com/media/coursera-logo-square.png'),
  sameAs: [
    'https://www.facebook.com/Coursera',
    'https://twitter.com/coursera',
    'https://www.linkedin.com/company/coursera',
    'https://www.instagram.com/coursera/',
    'https://www.youtube.com/user/coursera',
  ],
});

const createWebsiteSchema = (): WebSite => ({
  '@type': 'WebSite',
  name: 'Coursera',
  url: `${config.url.base}`,
  description: _t(
    'Learn online and earn valuable credentials from top universities like Yale, Michigan, Stanford, and leading companies like Google and IBM. Join Coursera for free and transform your career with degrees, certificates, Specializations, & MOOCs in data science, computer science, business, and dozens of other topics.'
  ),
  inLanguage: 'en-US',
});

const createCollectionPageSchema = ({ url, name }: { url: string; name: string }): CollectionPage => ({
  '@type': 'CollectionPage',
  url,
  name,
  inLanguage: 'en-US',
  provider: 'Coursera',
});

const createAuthorSchema = (author: AuthorProfile): Person => {
  const person: Person = {
    jobTitle: author.jobTitle ?? DEFAULT_ARTICLE_AUTHOR.jobTitle ?? '',
    description: author.description ?? undefined,
    '@id': author?.url ? `${author.url}#schema-author` : undefined,
    sameAs: author.externalWebsites?.filter(filterUndefined) ?? undefined,
    image: author.image ?? undefined,
    name: author.name ?? undefined,
    url: author.url ?? undefined,
    knowsAbout: author.knowsAbout?.map((knows) => knows.title),
    '@type': 'Person',
  };
  return clearEmptyFields(person);
};

const createAuthorsSchema = (authors: Array<AuthorProfile>) => {
  return authors.length ? authors.map(createAuthorSchema) : [createAuthorSchema(DEFAULT_ARTICLE_AUTHOR)];
};

export {
  createBreadcrumbsListSchema,
  createOrganizationSchema,
  createWebsiteSchema,
  createCollectionPageSchema,
  createGraph,
  createAuthorsSchema,
  createAuthorSchema,
};
